/**
 * Firma de licencias de la versión para consultorios EN EL NAVEGADOR (o en cualquier lugar con WebCrypto): el dueño elige su archivo
 * de llave privada, la licencia se firma ahí mismo y la llave no se envía a ningún servidor. Produce el mismo texto que
 * herramientas/licencias/licencias.mjs: `DONNA1.<datos en base64url>.<firma en base64url>`.
 */
import { sumarMeses } from "./mensajes.ts";
import type { DatosLicenciaLocal } from "./licencia-local.ts";
import { esPlanValido } from "./licencia-local.ts";
import { LISTA_FUNCIONES } from "./planes.ts";

export const CODIGO_INSTALACION = /^DONNA-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/;

const aBase64Url = (b: Uint8Array) => {
  let s = ""; for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export interface PedidoLicencia {
  instalacion: string; clinica: string; ruc?: string; hoy: string;
  meses?: number; hasta?: string; usuarios?: number | null; sedes?: number | null; funciones?: string[] | null;
  /** esencial, clinica o total */
  plan?: string | null;
  /** funciones contratadas aparte del plan */
  extras?: string[] | null;
  /** Fecha desde la que se cuentan los meses (para renovar sin perder los días que le quedaban). Por defecto, hoy. */
  desde?: string;
}

/** Datos de la licencia o la lista de problemas. */
export function datosDeLicencia(p: PedidoLicencia): { datos: DatosLicenciaLocal } | { errores: string[] } {
  const e: string[] = [];
  const instalacion = p.instalacion.trim().toUpperCase();
  if (!CODIGO_INSTALACION.test(instalacion)) e.push("El código de instalación debe verse así: DONNA-7K3Q-M2XP-9WTD (cópialo tal cual de Configuración → Licencia de la clínica).");
  const clinica = p.clinica.trim().slice(0, 120);
  if (!clinica) e.push("Escribe el nombre de la clínica.");
  let vence = "";
  if (p.hasta) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(p.hasta)) e.push("La fecha de vencimiento no es válida."); else vence = p.hasta;
  } else {
    const m = p.meses ?? 12;
    if (!Number.isInteger(m) || m < 1 || m > 60) e.push("Los meses deben ser un número entre 1 y 60."); else vence = sumarMeses(p.desde && p.desde > p.hoy ? p.desde : p.hoy, m);
  }
  if (vence && vence <= p.hoy) e.push("La fecha de vencimiento debe ser posterior a hoy.");
  const entero = (v: number | null | undefined, n: string) => { if (v === null || v === undefined) return null; if (!Number.isInteger(v) || v < 1) { e.push(`${n} debe ser un entero positivo.`); return null; } return v; };
  const usuarios = entero(p.usuarios, "Los usuarios"), sedes = entero(p.sedes, "Las sedes");
  if (p.plan && !esPlanValido(p.plan)) e.push("El plan debe ser Esencial, Clínica o Total.");
  if ((p.extras ?? []).some((f) => !(LISTA_FUNCIONES as readonly string[]).includes(f))) e.push("Hay una función extra que no existe.");
  if (e.length) return { errores: e };
  return { datos: { v: 1, instalacion, clinica, ruc: (p.ruc ?? "").replace(/\D/g, "").slice(0, 13), emitida: p.hoy, vence, usuarios, sedes, funciones: p.funciones?.length ? p.funciones : null,
    ...(p.plan ? { plan: p.plan as DatosLicenciaLocal["plan"], extras: p.extras?.length ? p.extras : null } : {}) } as DatosLicenciaLocal };
}

/** Firma los datos con la llave privada (texto PEM, formato PKCS#8 de Ed25519). Lanza un error claro si el archivo no sirve. */
export async function firmarLicencia(pemPrivada: string, datos: DatosLicenciaLocal): Promise<string> {
  const base = pemPrivada.replace(/-----[^-]+-----/g, "").replace(/\s+/g, "");
  if (!pemPrivada.includes("PRIVATE KEY") || !base) throw new Error("Ese archivo no es una llave privada de Donna (debe ser llave-privada.pem).");
  let der: Uint8Array;
  try { der = Uint8Array.from(atob(base), (c) => c.charCodeAt(0)); } catch { throw new Error("La llave privada está dañada."); }
  let llave: CryptoKey;
  try { llave = await crypto.subtle.importKey("pkcs8", der as BufferSource, { name: "Ed25519" }, false, ["sign"]); }
  catch { throw new Error("Este navegador no puede usar la llave (necesita Chrome, Edge, Safari o Firefox actualizados) o el archivo no es la llave de Donna."); }
  const cuerpo = new TextEncoder().encode(JSON.stringify(datos));
  const firma = new Uint8Array(await crypto.subtle.sign("Ed25519", llave, cuerpo as BufferSource));
  return `DONNA1.${aBase64Url(cuerpo)}.${aBase64Url(firma)}`;
}
